import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, Paper, TextField } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUICustomDataGrid } from '../../../../../atomic/molecules';
import { OperatorCommand } from '../../../../../services/command';
import { OperatorQuery } from '../../../../../services/query';
import { LUR } from '../../listUserRoles.classes';

const roleAddButtonSx = {
    borderRadius: '8px',
    minWidth: 72,
    height: 32,
    px: 1.5,
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'none' as const,
    color: '#fff',
    backgroundColor: '#2563eb',
    boxShadow: 'none',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.15s ease',
    '&:hover': {
        backgroundColor: '#1d4ed8',
        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    },
};

export const RoleModal: FC<any> = ({ applicationUserId, GetOperatorRolesList, roleList }: any) => {
    const { t } = useTranslation();
    const [rolesList, setRolesList] = useState<any>([]);
    const [roleListArray, setRoleListArray] = useState<any[]>(roleList);
    const [roleFilter, setRoleFilter] = useState('');

    const filteredRolesList = useMemo(() => {
        if (!roleFilter.trim()) return rolesList;
        const query = roleFilter.trim().toLowerCase();
        return rolesList.filter((item: any) => item.roleNameKey?.toLowerCase().includes(query));
    }, [rolesList, roleFilter]);

    const getRolesList = () => {
        OperatorQuery.GetRolesList()
            .then((res: any) => {
                if (res) {
                    const parsedData = res.model.data.map((item: any) => ({
                        roleId: item.roleId,
                        roleNameKey: item.roleNameKey,
                        applicationId: item.applicationId,
                    }));
                    const roleIds = roleListArray.map((item: any) => item.roleId);
                    const filteredParsedData = parsedData.filter((item: any) => !roleIds.includes(item.roleId));
                    setRolesList(filteredParsedData);
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            });
    };

    useEffect(() => {
        getRolesList();
    }, [roleListArray]);

    const kaydetOpr = async (paramss: any, userId: any) => {
        const params: any = {};
        params.userId = Number(userId);
        params.roleId = Number(paramss.roleId);
        OperatorCommand.PostAddOperatorToRole(params)
            .then(() => {
                setRoleListArray((prevState) => [
                    ...prevState,
                    {
                        roleId: paramss.roleId,
                        applicationId: paramss.applicationId,
                        roleNameKey: paramss.roleNameKey,
                    },
                ]);
                Swal.fire(t('general.added'), ' ', 'success');
                GetOperatorRolesList();
            })
            .catch((error: any) => {
                Swal.fire('', t('general.couldntDeleted'), 'error');
                console.log('Hata :>> ', error);
            });
    };

    const columns = [
        {
            field: 'roleId',
            headerName: 'ID',
            width: 72,
            sortable: false,
            renderCell: (params: any) => (
                <span className={LUR.cellRoleId} data-role-id={params.value}>
                    {params.value}
                </span>
            ),
        },
        {
            field: 'roleNameKey',
            headerName: t('general.roleName'),
            minWidth: 150,
            flex: 1,
            sortable: false,
            renderCell: (params: any) => (
                <Box
                    className={LUR.cellRoleName}
                    data-role-id={params.row?.roleId}
                    data-role-name={params.value}
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        pr: 1,
                    }}
                    title={params.value}
                >
                    {params.value}
                </Box>
            ),
        },
        {
            field: 'action',
            headerName: t('authorizationManagement.addRole'),
            width: 108,
            sortable: false,
            align: 'center' as const,
            headerAlign: 'center' as const,
            renderCell: (params: any) => (
                <Box
                    className={LUR.cellRoleAction}
                    data-role-id={params.row?.roleId}
                    data-role-name={params.row?.roleNameKey}
                    sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                >
                    <Button
                        className={LUR.btnRoleAdd}
                        data-role-id={params.row?.roleId}
                        data-role-name={params.row?.roleNameKey}
                        variant="contained"
                        size="small"
                        disableElevation
                        onClick={() => kaydetOpr(params.row, applicationUserId)}
                        sx={roleAddButtonSx}
                    >
                        {t('general.add')}
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Box className={LUR.roleModal} data-application-user-id={applicationUserId}>
            <Paper
                elevation={0}
                className={LUR.roleModalFilter}
                sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FAFAFA',
                }}
            >
                <TextField
                    className={LUR.inputRoleModalFilter}
                    size="small"
                    fullWidth
                    placeholder={t('authorizationManagement.listUser.filterRoleName') as string}
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FilterListRoundedIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: roleFilter ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setRoleFilter('')}
                                        aria-label="clear filter"
                                    >
                                        <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: '#fff',
                        },
                    }}
                />
            </Paper>
            <Paper
                elevation={0}
                className={LUR.roleModalGrid}
                sx={{
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    '& .MuiDataGrid-root': {
                        border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#F9FAFB',
                    },
                    '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center',
                    },
                }}
            >
                <SCUICustomDataGrid getRowId={(row: any) => row.roleId} rows={filteredRolesList} columns={columns} />
            </Paper>
        </Box>
    );
};
